import { useState, useEffect, useMemo, useCallback } from 'react';
import { statesService, SimpleState, CountryStatesGroup } from '../services/statesService';

export const useStates = () => {
  const [groupedStates, setGroupedStates] = useState<CountryStatesGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingStateCode, setSavingStateCode] = useState<string | null>(null);

  const loadStates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await statesService.getStates();
      setGroupedStates(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load states');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLotterySoftwareProvider = useCallback(async (stateCode: string, provider: string) => {
    const normalized = provider.trim();
    const payload = normalized.length ? normalized.slice(0, 30) : null;

    try {
      setSavingStateCode(stateCode);
      await statesService.updateState(stateCode, payload);
      setGroupedStates((current) => current.map((group) => ({
        ...group,
        states: group.states.map((s) => (
          s.state_code === stateCode ? { ...s, lottery_software_provider: payload } : s
        )),
      })));
    } finally {
      setSavingStateCode(null);
    }
  }, []);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  // Provide a flat list for legacy consumers
  const states: SimpleState[] = useMemo(() => {
    return groupedStates.flatMap((g) => g.states);
  }, [groupedStates]);

  return {
    groupedStates,
    states,
    loading,
    error,
    loadStates,
    savingStateCode,
    updateLotterySoftwareProvider,
  };
};
